export const GRAVITY_IN_PER_S2 = 386.09;
export const SPEED_OF_SOUND_FT_PER_S = 1100;
export const MAX_TRAJECTORY_STEPS = 100_000;
export const MAX_LAUNCH_ANGLES = 181;

export type Target = { kind: 'plane'; standoffFt: number } | { kind: 'rectangle'; standoffFt: number; heightFt: number; widthFt: number };

export interface TrajectoryInput {
  airDensityLbMs2PerIn4: number;
  launchHeightFt: number;
  launchVelocityFtPerS: number;
  launchAnglesDeg: number[];
  fragmentWeightLbf: number;
  dragAreaIn2: number;
  dragCoefficient: number;
  distanceFactor: number;
  target: Target;
}

export interface Trajectory {
  angleDeg: number;
  xFt: number[];
  zFt: number[];
  vxFtPerS: number[];
  vzFtPerS: number[];
  timeS: number[];
  rangeFt: number;
  maximumHeightFt: number;
}

export interface ImpactPoint {
  angleDeg: number;
  xFt: number;
  zFt: number;
  velocityFtPerS: number;
  momentumLbfS: number;
  timeS: number;
}

export interface TrajectoryFamily {
  launchVelocityFtPerS: number;
  trajectories: Trajectory[];
  maximumRangeFt: number;
  maximumHeightFt: number;
  impacts: ImpactPoint[];
}

export interface TrajectoryAnalysis {
  nominal: TrajectoryFamily;
  design: TrajectoryFamily;
  distanceFactor: number;
  specifiedDesignRangeFt: number;
  equivalentVelocityFactor: number;
  warnings: string[];
}

export type TrajectoryRangeInput = Pick<TrajectoryInput, 'airDensityLbMs2PerIn4' | 'launchHeightFt' | 'launchVelocityFtPerS' | 'fragmentWeightLbf' | 'dragAreaIn2' | 'dragCoefficient'> & {
  launchAngleDeg: number;
};

export interface TrajectoryRangeResult {
  rangeFt: number;
  maximumHeightFt: number;
  flightTimeS: number;
  groundImpactVelocityFtPerS: number;
  warnings: string[];
}

type State = [xIn: number, zIn: number, vxInPerS: number, vzInPerS: number];

function degreesToRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

function maximum(values: readonly number[]): number {
  let result = -Infinity;
  for (const value of values) result = Math.max(result, value);
  return result;
}

function assertFinitePositive(value: number, label: string): void {
  if (!Number.isFinite(value) || value <= 0) throw new Error(`${label} must be finite and greater than zero.`);
}

export function parseLaunchAngles(source: string): number[] {
  const angles: number[] = [];
  const tokens = source
    .trim()
    .split(/[\s,]+/)
    .filter(Boolean);

  for (const token of tokens) {
    if (!token.includes(':')) {
      const angle = Number(token);
      if (!Number.isFinite(angle)) throw new Error(`Invalid launch angle: ${token}.`);
      angles.push(angle);
      continue;
    }

    const parts = token.split(':');
    if (parts.length !== 3) throw new Error(`Angle range must use start:step:end: ${token}.`);
    const [start, step, end] = parts.map(Number);
    if (![start, step, end].every(Number.isFinite)) throw new Error(`Invalid launch-angle range: ${token}.`);
    if (step === 0) throw new Error(`Launch-angle range step cannot be zero: ${token}.`);
    if ((end - start) * step < 0) throw new Error(`Launch-angle range step points away from its end: ${token}.`);

    const tolerance = Math.abs(step) * 1e-10;
    for (let angle = start; step > 0 ? angle <= end + tolerance : angle >= end - tolerance; angle += step) {
      angles.push(angle);
      if (angles.length > MAX_LAUNCH_ANGLES) throw new Error(`No more than ${MAX_LAUNCH_ANGLES} launch angles may be analyzed.`);
    }
  }

  if (angles.length === 0) throw new Error('Provide at least one launch angle.');
  if (angles.length > MAX_LAUNCH_ANGLES) throw new Error(`No more than ${MAX_LAUNCH_ANGLES} launch angles may be analyzed.`);
  if (angles.some(angle => angle < -90 || angle > 90)) throw new Error('Launch angles must be between -90 and 90 degrees.');
  return angles;
}

export function parseTarget(source: string): Target {
  const values = source
    .trim()
    .split(/[\s,]+/)
    .filter(Boolean)
    .map(Number);
  if (values.some(value => !Number.isFinite(value))) throw new Error('Target values must be finite numbers.');

  if (values.length === 1) {
    if (values[0] < 0) throw new Error('Target standoff must be greater than or equal to zero.');
    return { kind: 'plane', standoffFt: values[0] };
  }

  if (values.length === 3) {
    const [standoffFt, heightFt, widthFt] = values;
    if (standoffFt < 0) throw new Error('Target standoff must be greater than or equal to zero.');
    if (heightFt <= 0 || widthFt <= 0) throw new Error('Target height and width must be greater than zero.');
    return { kind: 'rectangle', standoffFt, heightFt, widthFt };
  }

  throw new Error('Target must be one standoff value or standoff,height,width, in feet.');
}

function simulateTrajectory(input: TrajectoryInput, angleDeg: number, launchVelocityFtPerS: number): Trajectory {
  const airDensityLbS2PerIn4 = input.airDensityLbMs2PerIn4 / 1e6;
  const massLbfS2PerIn = input.fragmentWeightLbf / GRAVITY_IN_PER_S2;
  const dragFactor = (0.5 * airDensityLbS2PerIn4 * input.dragCoefficient * input.dragAreaIn2) / massLbfS2PerIn;
  const angleRad = degreesToRadians(angleDeg);

  function derivatives(state: State): State {
    const [, , vx, vz] = state;
    const speed = Math.hypot(vx, vz);
    return [vx, vz, -dragFactor * speed * vx, -GRAVITY_IN_PER_S2 - dragFactor * speed * vz];
  }

  function rk4Step(state: State, timeStepS: number): State {
    const k1 = derivatives(state);
    const state2 = state.map((value, index) => value + 0.5 * timeStepS * k1[index]) as State;
    const k2 = derivatives(state2);
    const state3 = state.map((value, index) => value + 0.5 * timeStepS * k2[index]) as State;
    const k3 = derivatives(state3);
    const state4 = state.map((value, index) => value + timeStepS * k3[index]) as State;
    const k4 = derivatives(state4);
    return state.map((value, index) => value + (timeStepS * (k1[index] + 2 * k2[index] + 2 * k3[index] + k4[index])) / 6) as State;
  }

  let state: State = [0, input.launchHeightFt * 12, launchVelocityFtPerS * 12 * Math.cos(angleRad), launchVelocityFtPerS * 12 * Math.sin(angleRad)];
  const timeStepS = Math.min(1 / SPEED_OF_SOUND_FT_PER_S / 2, 10 / Math.max(Math.abs(launchVelocityFtPerS), 1e-9));
  const xIn = [state[0]];
  const zIn = [state[1]];
  const vxInPerS = [state[2]];
  const vzInPerS = [state[3]];
  const timeS = [0];
  let reachedGround = false;

  for (let step = 0; step < MAX_TRAJECTORY_STEPS && !reachedGround; step += 1) {
    const oldState = state;
    const oldTime = timeS[timeS.length - 1];
    const nextState = rk4Step(oldState, timeStepS);
    if (nextState.some(value => !Number.isFinite(value))) throw new Error(`Trajectory at ${angleDeg}° produced a non-finite state.`);

    if (oldState[1] > 0 && nextState[1] <= 0) {
      const fraction = oldState[1] / (oldState[1] - nextState[1]);
      state = oldState.map((value, index) => value + fraction * (nextState[index] - value)) as State;
      state[1] = 0;
      timeS.push(oldTime + fraction * timeStepS);
      reachedGround = true;
    } else {
      state = nextState;
      timeS.push(oldTime + timeStepS);
      reachedGround = state[1] <= 0;
    }

    xIn.push(state[0]);
    zIn.push(state[1]);
    vxInPerS.push(state[2]);
    vzInPerS.push(state[3]);
  }

  if (!reachedGround) throw new Error(`Trajectory at ${angleDeg}° did not reach the ground within ${MAX_TRAJECTORY_STEPS} steps.`);

  return {
    angleDeg,
    xFt: xIn.map(value => value / 12),
    zFt: zIn.map(value => value / 12),
    vxFtPerS: vxInPerS.map(value => value / 12),
    vzFtPerS: vzInPerS.map(value => value / 12),
    timeS,
    rangeFt: maximum(xIn) / 12,
    maximumHeightFt: maximum(zIn) / 12
  };
}

function interpolateImpact(trajectory: Trajectory, startIndex: number, endIndex: number, fraction: number, fragmentWeightLbf: number): ImpactPoint {
  const interpolate = (values: readonly number[]) => values[startIndex] + fraction * (values[endIndex] - values[startIndex]);
  const vxFtPerS = interpolate(trajectory.vxFtPerS);
  const vzFtPerS = interpolate(trajectory.vzFtPerS);
  const velocityFtPerS = Math.hypot(vxFtPerS, vzFtPerS);
  return {
    angleDeg: trajectory.angleDeg,
    xFt: interpolate(trajectory.xFt),
    zFt: interpolate(trajectory.zFt),
    velocityFtPerS,
    momentumLbfS: (fragmentWeightLbf / (GRAVITY_IN_PER_S2 / 12)) * velocityFtPerS,
    timeS: interpolate(trajectory.timeS)
  };
}

function pointInsideRectangle(x: number, z: number, target: Extract<Target, { kind: 'rectangle' }>): boolean {
  return x >= target.standoffFt && x <= target.standoffFt + target.widthFt && z >= 0 && z <= target.heightFt;
}

function segmentRectangleEntry(x0: number, z0: number, x1: number, z1: number, target: Extract<Target, { kind: 'rectangle' }>): number | null {
  if (pointInsideRectangle(x0, z0, target)) return 0;
  const dx = x1 - x0;
  const dz = z1 - z0;
  let enter = 0;
  let exit = 1;
  const p = [-dx, dx, -dz, dz];
  const q = [x0 - target.standoffFt, target.standoffFt + target.widthFt - x0, z0, target.heightFt - z0];

  for (let index = 0; index < 4; index += 1) {
    if (Math.abs(p[index]) < 1e-14) {
      if (q[index] < 0) return null;
      continue;
    }
    const ratio = q[index] / p[index];
    if (p[index] < 0) {
      if (ratio > exit) return null;
      enter = Math.max(enter, ratio);
    } else {
      if (ratio < enter) return null;
      exit = Math.min(exit, ratio);
    }
  }

  return enter >= -1e-12 && enter <= 1 + 1e-12 ? Math.max(0, Math.min(1, enter)) : null;
}

export function findFirstTargetImpact(trajectory: Trajectory, target: Target, fragmentWeightLbf: number): ImpactPoint | null {
  for (let index = 1; index < trajectory.xFt.length; index += 1) {
    let fraction: number | null = null;
    if (target.kind === 'plane') {
      const x0 = trajectory.xFt[index - 1];
      const x1 = trajectory.xFt[index];
      if ((x0 - target.standoffFt) * (x1 - target.standoffFt) <= 0 && Math.abs(x1 - x0) > 1e-14) fraction = (target.standoffFt - x0) / (x1 - x0);
    } else {
      fraction = segmentRectangleEntry(trajectory.xFt[index - 1], trajectory.zFt[index - 1], trajectory.xFt[index], trajectory.zFt[index], target);
    }
    if (fraction !== null && fraction >= 0 && fraction <= 1) return interpolateImpact(trajectory, index - 1, index, fraction, fragmentWeightLbf);
  }
  return null;
}

function calculateFamily(input: TrajectoryInput, launchVelocityFtPerS: number): TrajectoryFamily {
  const trajectories = input.launchAnglesDeg.map(angle => simulateTrajectory(input, angle, launchVelocityFtPerS));
  return {
    launchVelocityFtPerS,
    trajectories,
    maximumRangeFt: maximum(trajectories.map(trajectory => trajectory.rangeFt)),
    maximumHeightFt: maximum(trajectories.map(trajectory => trajectory.maximumHeightFt)),
    impacts: trajectories.map(trajectory => findFirstTargetImpact(trajectory, input.target, input.fragmentWeightLbf)).filter((impact): impact is ImpactPoint => impact !== null)
  };
}

function solveEquivalentDesignVelocity(input: TrajectoryInput, nominal: TrajectoryFamily): number {
  if (Math.abs(input.distanceFactor - 1) <= 1e-10) return input.launchVelocityFtPerS;
  const targetRangeFt = input.distanceFactor * nominal.maximumRangeFt;
  const rangeAt = (velocity: number) => calculateFamily(input, Math.max(velocity, 0)).maximumRangeFt;
  let lowerVelocity: number;
  let upperVelocity: number;

  if (input.distanceFactor > 1) {
    lowerVelocity = input.launchVelocityFtPerS;
    upperVelocity = Math.max(input.launchVelocityFtPerS * Math.sqrt(input.distanceFactor), input.launchVelocityFtPerS * 1.02);
    let upperRangeFt = rangeAt(upperVelocity);
    for (let expansion = 0; upperRangeFt < targetRangeFt && expansion < 24; expansion += 1) {
      upperVelocity *= 1.25;
      upperRangeFt = rangeAt(upperVelocity);
    }
    if (upperRangeFt < targetRangeFt) throw new Error('Unable to bracket an equivalent design launch velocity for the requested DDESB distance factor.');
  } else {
    lowerVelocity = 0;
    upperVelocity = input.launchVelocityFtPerS;
    if (targetRangeFt <= rangeAt(lowerVelocity)) throw new Error('Unable to bracket an equivalent design launch velocity below the nominal velocity.');
  }

  let trialVelocity = input.launchVelocityFtPerS;
  const rangeToleranceFt = Math.max(0.01, 1e-4 * targetRangeFt);
  for (let iteration = 0; iteration < 40; iteration += 1) {
    trialVelocity = 0.5 * (lowerVelocity + upperVelocity);
    const trialRangeFt = rangeAt(trialVelocity);
    if (Math.abs(trialRangeFt - targetRangeFt) <= rangeToleranceFt) return trialVelocity;
    if (trialRangeFt < targetRangeFt) lowerVelocity = trialVelocity;
    else upperVelocity = trialVelocity;
  }
  return trialVelocity;
}

export function calculateTrajectoryAnalysis(input: TrajectoryInput): TrajectoryAnalysis {
  assertFinitePositive(input.airDensityLbMs2PerIn4, 'Air density');
  assertFinitePositive(input.launchVelocityFtPerS, 'Launch velocity');
  assertFinitePositive(input.fragmentWeightLbf, 'Fragment weight');
  assertFinitePositive(input.dragAreaIn2, 'Drag area');
  assertFinitePositive(input.dragCoefficient, 'Drag coefficient');
  assertFinitePositive(input.distanceFactor, 'DDESB distance factor');
  if (!Number.isFinite(input.launchHeightFt) || input.launchHeightFt < 0) throw new Error('Launch height must be finite and nonnegative.');
  if (input.launchAnglesDeg.length === 0 || input.launchAnglesDeg.length > MAX_LAUNCH_ANGLES) throw new Error(`Provide between 1 and ${MAX_LAUNCH_ANGLES} launch angles.`);

  const nominal = calculateFamily(input, input.launchVelocityFtPerS);
  if (!(nominal.maximumRangeFt > 0)) throw new Error('The supplied angles do not produce a positive nominal trajectory range.');
  const designVelocityFtPerS = solveEquivalentDesignVelocity(input, nominal);
  const design = calculateFamily(input, designVelocityFtPerS);
  const warnings: string[] = [];
  if (input.launchVelocityFtPerS > SPEED_OF_SOUND_FT_PER_S) warnings.push('Nominal initial velocity exceeds Mach 1; the simplified UFC/DOE trajectory equations are outside their stated applicability range.');
  if (designVelocityFtPerS > SPEED_OF_SOUND_FT_PER_S) warnings.push('Equivalent DDESB design velocity exceeds Mach 1; the simplified UFC/DOE trajectory equations are outside their stated applicability range.');

  return {
    nominal,
    design,
    distanceFactor: input.distanceFactor,
    specifiedDesignRangeFt: input.distanceFactor * nominal.maximumRangeFt,
    equivalentVelocityFactor: designVelocityFtPerS / input.launchVelocityFtPerS,
    warnings
  };
}

export function calculateTrajectoryRange(input: TrajectoryRangeInput): TrajectoryRangeResult {
  assertFinitePositive(input.airDensityLbMs2PerIn4, 'Air density');
  assertFinitePositive(input.launchVelocityFtPerS, 'Launch velocity');
  assertFinitePositive(input.fragmentWeightLbf, 'Fragment weight');
  assertFinitePositive(input.dragAreaIn2, 'Drag area');
  assertFinitePositive(input.dragCoefficient, 'Drag coefficient');
  if (!Number.isFinite(input.launchHeightFt) || input.launchHeightFt < 0) throw new Error('Launch height must be finite and nonnegative.');
  if (!Number.isFinite(input.launchAngleDeg) || input.launchAngleDeg < -90 || input.launchAngleDeg > 90) throw new Error('Launch angle must be between -90 and 90 degrees.');

  const trajectory = simulateTrajectory(
    {
      ...input,
      launchAnglesDeg: [input.launchAngleDeg],
      distanceFactor: 1,
      target: { kind: 'plane', standoffFt: 0 }
    },
    input.launchAngleDeg,
    input.launchVelocityFtPerS
  );
  const finalIndex = trajectory.vxFtPerS.length - 1;
  const warnings = input.launchVelocityFtPerS > SPEED_OF_SOUND_FT_PER_S ? ['Initial velocity exceeds Mach 1; the simplified UFC/DOE trajectory equations are outside their stated applicability range.'] : [];

  return {
    rangeFt: trajectory.rangeFt,
    maximumHeightFt: trajectory.maximumHeightFt,
    flightTimeS: trajectory.timeS[finalIndex],
    groundImpactVelocityFtPerS: Math.hypot(trajectory.vxFtPerS[finalIndex], trajectory.vzFtPerS[finalIndex]),
    warnings
  };
}
